/**
 * API wrapper for the Projekte module (Phase 5 Stufe I, Slice 5,
 * 2026-05-03). Projekte sind Schadens-/Koordinations-Container, die
 * mehrere Aufträge desselben Kunden bündeln. Backend-Permissions:
 * `projekt:read` / `projekt:write` (admin/buero).
 */
import { api } from './api';

export type ProjektStatus = 'offen' | 'in_arbeit' | 'abgeschlossen' | 'storniert';
export const PROJEKT_STATUS: readonly ProjektStatus[] = [
  'offen',
  'in_arbeit',
  'abgeschlossen',
  'storniert',
];

export const PROJEKT_STATUS_LABEL: Record<ProjektStatus, string> = {
  offen: 'Offen',
  in_arbeit: 'In Arbeit',
  abgeschlossen: 'Abgeschlossen',
  storniert: 'Storniert',
};

export const PROJEKT_STATUS_BADGE: Record<ProjektStatus, string> = {
  offen: 'bg-blue-100 text-blue-800 border-blue-200',
  in_arbeit: 'bg-amber-100 text-amber-800 border-amber-200',
  abgeschlossen: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  storniert: 'bg-rose-100 text-rose-800 border-rose-200',
};

export type MargeTyp = 'aufschlag' | 'pauschale';
export const MARGE_TYPEN: readonly MargeTyp[] = ['aufschlag', 'pauschale'];
export const MARGE_TYP_LABEL: Record<MargeTyp, string> = {
  aufschlag: 'Aufschlag (%)',
  pauschale: 'Pauschale (EUR)',
};

export interface Projekt {
  id: string;
  kundeId: string;
  objektId: string | null;
  bezeichnung: string;
  schadennummer: string | null;
  versicherungKundeId: string | null;
  sachbearbeiter: string | null;
  status: ProjektStatus;
  margeTyp: MargeTyp | null;
  margeWert: string | null;
  createdAt: string;
  updatedAt: string;
}

interface CreateProjektInput {
  kundeId: string;
  objektId?: string;
  bezeichnung: string;
  schadennummer?: string;
  versicherungKundeId?: string;
  sachbearbeiter?: string;
  margeTyp?: MargeTyp;
  margeWert?: string;
}

interface UpdateProjektInput {
  bezeichnung?: string;
  schadennummer?: string;
  versicherungKundeId?: string;
  sachbearbeiter?: string;
  status?: ProjektStatus;
  margeTyp?: MargeTyp;
  margeWert?: string;
}

interface ProjektAuftragRef {
  id: string;
  titel: string;
}

export const projekteApi = {
  async list(): Promise<{ items: Projekt[] }> {
    return api<{ items: Projekt[] }>('/api/v1/projekte');
  },

  async getById(id: string): Promise<{ item: Projekt }> {
    return api<{ item: Projekt }>(`/api/v1/projekte/${id}`);
  },

  async create(input: CreateProjektInput): Promise<{ item: Projekt }> {
    return api<{ item: Projekt }>('/api/v1/projekte', { method: 'POST', body: input });
  },

  async update(id: string, input: UpdateProjektInput): Promise<{ item: Projekt }> {
    return api<{ item: Projekt }>(`/api/v1/projekte/${id}`, { method: 'PATCH', body: input });
  },

  async listAuftraege(id: string): Promise<{ items: ProjektAuftragRef[] }> {
    return api<{ items: ProjektAuftragRef[] }>(`/api/v1/projekte/${id}/auftraege`);
  },

  async addAuftrag(id: string, auftragId: string): Promise<void> {
    await api<void>(`/api/v1/projekte/${id}/auftraege`, {
      method: 'POST',
      body: { auftragId },
    });
  },

  async removeAuftrag(id: string, auftragId: string): Promise<void> {
    await api<void>(`/api/v1/projekte/${id}/auftraege/${auftragId}`, {
      method: 'DELETE',
    });
  },
};
